import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect, useCallback } from "react";
import { BookOpen, Plus, Trash2, ChevronDown, ChevronUp, Loader2, CheckCircle2, X, Target, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import jsPDF from "jspdf";

type CpdType = "reflect" | "plan" | "act" | "evaluate";
type CpdCategory = "clinical" | "professional" | "leadership";

interface CpdActivity {
  id: number;
  title: string;
  date: string;
  type: CpdType;
  category: CpdCategory;
  hours: number;
  learningOutcome: string;
  patientBenefit: string;
  createdAt: string;
}

interface CpdSummary {
  count: number;
  reflect: number;
  plan: number;
  act: number;
  evaluate: number;
  year: number;
}

const TYPE_LABELS: Record<CpdType, string> = {
  reflect: "Reflect",
  plan: "Plan",
  act: "Act",
  evaluate: "Evaluate",
};

const TYPE_COLORS: Record<CpdType, string> = {
  reflect: "bg-violet-50 text-violet-700 border-violet-200",
  plan: "bg-blue-50 text-blue-700 border-blue-200",
  act: "bg-emerald-50 text-emerald-700 border-emerald-200",
  evaluate: "bg-amber-50 text-amber-700 border-amber-200",
};

const CATEGORY_LABELS: Record<CpdCategory, string> = {
  clinical: "Clinical",
  professional: "Professional",
  leadership: "Leadership",
};

const ANNUAL_TARGET = 9;
const currentYear = new Date().getFullYear();

function exportCpdToPdf(activities: CpdActivity[], summary: CpdSummary, year: number) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = margin;

  const hex = (h: string) => {
    const r = parseInt(h.slice(1, 3), 16);
    const g = parseInt(h.slice(3, 5), 16);
    const b = parseInt(h.slice(5, 7), 16);
    return [r, g, b] as [number, number, number];
  };

  const violet = hex("#5b21b6");
  const violetLight = hex("#ede9fe");
  const slate700 = hex("#334155");
  const slate500 = hex("#64748b");
  const slate200 = hex("#e2e8f0");
  const white = hex("#ffffff");
  const emerald = hex("#065f46");
  const emeraldLight = hex("#d1fae5");

  const typeColours: Record<CpdType, [string, string]> = {
    reflect: ["#5b21b6", "#ede9fe"],
    plan: ["#1e40af", "#dbeafe"],
    act: ["#065f46", "#d1fae5"],
    evaluate: ["#92400e", "#fef3c7"],
  };

  const ensurePage = (neededMm: number) => {
    if (y + neededMm > pageH - margin - 12) {
      doc.addPage();
      y = margin;
    }
  };

  const drawLine = (col: [number, number, number] = slate200) => {
    doc.setDrawColor(...col);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageW - margin, y);
  };

  doc.setFillColor(...violet);
  doc.rect(0, 0, pageW, 28, "F");

  doc.setTextColor(...white);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("RheumHub", margin, 12);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("GPhC CPD Activity Log", margin, 20);

  doc.setFontSize(9);
  doc.text(`${year}`, pageW - margin, 12, { align: "right" });
  doc.text(`Exported ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`, pageW - margin, 20, { align: "right" });

  y = 38;

  doc.setFillColor(...violetLight);
  doc.roundedRect(margin, y, contentW, 26, 3, 3, "F");

  const colW = contentW / 5;
  const statLabels: [string, string | number][] = [
    ["Total Entries", summary.count],
    ["Reflect", summary.reflect],
    ["Plan", summary.plan],
    ["Act", summary.act],
    ["Evaluate", summary.evaluate],
  ];

  statLabels.forEach(([label, val], i) => {
    const x = margin + i * colW + colW / 2;
    doc.setTextColor(...violet);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text(String(val), x, y + 11, { align: "center" });
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...slate500);
    doc.text(label, x, y + 18, { align: "center" });
  });

  y += 32;

  const progressPct = Math.min((summary.count / ANNUAL_TARGET) * 100, 100);
  const barW = contentW;
  doc.setFillColor(...slate200);
  doc.roundedRect(margin, y, barW, 4, 2, 2, "F");

  const fillHex = summary.count >= ANNUAL_TARGET ? "#10b981" : summary.count >= 5 ? "#7c3aed" : "#f59e0b";
  const fillRgb = hex(fillHex);
  doc.setFillColor(...fillRgb);
  doc.roundedRect(margin, y, barW * (progressPct / 100), 4, 2, 2, "F");

  doc.setFontSize(7);
  doc.setTextColor(...slate500);
  doc.text(`GPhC minimum: 9 entries/year · Progress: ${summary.count} / ${ANNUAL_TARGET}`, margin, y + 9);

  y += 15;
  drawLine();
  y += 6;

  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...slate700);
  doc.text(`CPD Activities (${activities.length})`, margin, y);
  y += 8;

  const dateFormatted = (d: string) =>
    new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  activities.forEach((a, idx) => {
    const [fgHex, bgHex] = typeColours[a.type];
    const fgRgb = hex(fgHex);
    const bgRgb = hex(bgHex);

    const outcomeLines = doc.splitTextToSize(a.learningOutcome, contentW - 6);
    const benefitLines = doc.splitTextToSize(a.patientBenefit, contentW - 6);
    const lineH = 4.5;
    const blockH = 10 + (outcomeLines.length + benefitLines.length + 4) * lineH + 6;

    ensurePage(blockH + 4);

    doc.setFillColor(...(idx % 2 === 0 ? white : [248, 250, 252] as [number, number, number]));
    doc.rect(margin, y, contentW, blockH, "F");

    doc.setDrawColor(...slate200);
    doc.setLineWidth(0.2);
    doc.rect(margin, y, contentW, blockH);

    const badgeW = 22;
    doc.setFillColor(...bgRgb);
    doc.roundedRect(margin + 3, y + 3, badgeW, 5.5, 1.5, 1.5, "F");
    doc.setTextColor(...fgRgb);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(TYPE_LABELS[a.type].toUpperCase(), margin + 3 + badgeW / 2, y + 7.2, { align: "center" });

    const catBadgeX = margin + 3 + badgeW + 3;
    const catBadgeW = 24;
    doc.setFillColor(...slate200);
    doc.roundedRect(catBadgeX, y + 3, catBadgeW, 5.5, 1.5, 1.5, "F");
    doc.setTextColor(...slate500);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(CATEGORY_LABELS[a.category].toUpperCase(), catBadgeX + catBadgeW / 2, y + 7.2, { align: "center" });

    doc.setTextColor(...slate700);
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.text(a.title, margin + 3, y + 13.5);

    doc.setTextColor(...slate500);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(dateFormatted(a.date), margin + 3, y + 18.5);

    doc.setTextColor(...violet);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(`${a.hours}h`, pageW - margin - 3, y + 14, { align: "right" });

    let innerY = y + 22;

    doc.setTextColor(...slate500);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text("Learning Outcome", margin + 3, innerY);
    innerY += lineH;
    doc.setTextColor(...slate700);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    outcomeLines.forEach((line: string) => { doc.text(line, margin + 3, innerY); innerY += lineH; });

    innerY += 2;
    doc.setTextColor(...slate500);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.text("Benefit to Patients / Public", margin + 3, innerY);
    innerY += lineH;
    doc.setTextColor(...slate700);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    benefitLines.forEach((line: string) => { doc.text(line, margin + 3, innerY); innerY += lineH; });

    y += blockH + 3;
  });

  ensurePage(20);
  y += 4;
  drawLine();
  y += 5;

  const metStatus = summary.count >= ANNUAL_TARGET;
  doc.setFillColor(...(metStatus ? emeraldLight : violetLight));
  doc.roundedRect(margin, y, contentW, 16, 2, 2, "F");
  doc.setTextColor(...(metStatus ? emerald : violet));
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.text(
    metStatus
      ? `✓ GPhC minimum of 9 entries met for ${year}`
      : `GPhC minimum of 9 entries per year not yet reached (${summary.count} / ${ANNUAL_TARGET})`,
    margin + 4,
    y + 6,
  );
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...slate500);
  doc.text(
    "This record is for your personal reference only. You must also maintain your formal CPD record on the GPhC portal at pharmacyregulation.org.",
    margin + 4,
    y + 11,
    { maxWidth: contentW - 8 },
  );
  y += 22;

  const totalPages = (doc.internal as unknown as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setTextColor(...slate500);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(`RheumHub — GPhC CPD Log ${year} — Page ${p} of ${totalPages}`, pageW / 2, pageH - 8, { align: "center" });
  }

  doc.save(`GPhC-CPD-Log-${year}.pdf`);
}

interface AddFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

function AddActivityForm({ onSaved, onCancel }: AddFormProps) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [type, setType] = useState<CpdType>("reflect");
  const [category, setCategory] = useState<CpdCategory>("clinical");
  const [hours, setHours] = useState("");
  const [learningOutcome, setLearningOutcome] = useState("");
  const [patientBenefit, setPatientBenefit] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !date || !hours || !learningOutcome.trim() || !patientBenefit.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/pharmacist/cpd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          date,
          type,
          category,
          hours: parseFloat(hours),
          learningOutcome: learningOutcome.trim(),
          patientBenefit: patientBenefit.trim(),
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      onSaved();
    } catch {
      setError("Failed to save activity. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-violet-200 p-6 space-y-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">Add CPD Activity</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="pharm-cpd-title" className="block text-xs font-medium text-slate-600 mb-1.5">Activity Title *</label>
          <input
            id="pharm-cpd-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. DMARDs in Rheumatoid Arthritis — CPPE Programme"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div>
          <label htmlFor="pharm-cpd-date" className="block text-xs font-medium text-slate-600 mb-1.5">Date *</label>
          <input
            id="pharm-cpd-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div>
          <label htmlFor="pharm-cpd-hours" className="block text-xs font-medium text-slate-600 mb-1.5">Hours *</label>
          <input
            id="pharm-cpd-hours"
            type="number"
            min="0.5"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="e.g. 2"
            required
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
        </div>
        <div>
          <label htmlFor="pharm-cpd-type" className="block text-xs font-medium text-slate-600 mb-1.5">GPhC CPD Cycle Step *</label>
          <select
            id="pharm-cpd-type"
            value={type}
            onChange={(e) => setType(e.target.value as CpdType)}
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="reflect">Reflect — identify a learning need</option>
            <option value="plan">Plan — plan how to address the need</option>
            <option value="act">Act — undertake the learning activity</option>
            <option value="evaluate">Evaluate — evaluate the outcome</option>
          </select>
        </div>
        <div>
          <label htmlFor="pharm-cpd-category" className="block text-xs font-medium text-slate-600 mb-1.5">Category *</label>
          <select
            id="pharm-cpd-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as CpdCategory)}
            className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="clinical">Clinical</option>
            <option value="professional">Professional</option>
            <option value="leadership">Leadership</option>
          </select>
        </div>
        <div className="col-span-2">
          <label htmlFor="pharm-cpd-outcome" className="block text-xs font-medium text-slate-600 mb-1.5">Learning Outcome *</label>
          <textarea
            id="pharm-cpd-outcome"
            value={learningOutcome}
            onChange={(e) => setLearningOutcome(e.target.value)}
            rows={3}
            placeholder="What did you learn? What knowledge, skills or attitudes did you develop?"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>
        <div className="col-span-2">
          <label htmlFor="pharm-cpd-benefit" className="block text-xs font-medium text-slate-600 mb-1.5">Benefit to Patients / Public *</label>
          <textarea
            id="pharm-cpd-benefit"
            value={patientBenefit}
            onChange={(e) => setPatientBenefit(e.target.value)}
            rows={2}
            placeholder="How has this learning benefited or could benefit patients and the public?"
            required
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

      <div className="flex gap-3">
        <Button type="submit" disabled={saving} className="bg-violet-700 hover:bg-violet-800 text-white gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {saving ? "Saving..." : "Save Activity"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function SummaryBar({ summary, year }: { summary: CpdSummary; year: number }) {
  const pct = Math.min((summary.count / ANNUAL_TARGET) * 100, 100);
  const colour = summary.count >= ANNUAL_TARGET ? "bg-emerald-500" : summary.count >= 5 ? "bg-violet-500" : "bg-amber-500";

  return (
    <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
      <div className="flex items-center gap-3 mb-4">
        <BookOpen className="h-8 w-8 text-violet-300" />
        <div>
          <h2 className="text-xl font-bold">GPhC CPD Activity Log</h2>
          <p className="text-violet-300 text-sm">{year} · GPhC CPD Cycle (Reflect → Plan → Act → Evaluate)</p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="bg-white/10 rounded-xl p-3 col-span-1">
          <p className="text-2xl font-bold">{summary.count}</p>
          <p className="text-xs text-violet-300">Total entries</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.reflect}</p>
          <p className="text-xs text-violet-300">Reflect</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.plan}</p>
          <p className="text-xs text-violet-300">Plan</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.act}</p>
          <p className="text-xs text-violet-300">Act</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <p className="text-xl font-bold">{summary.evaluate}</p>
          <p className="text-xs text-violet-300">Evaluate</p>
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-sm text-violet-200">
          <span>Progress to GPhC minimum 9 entries per year</span>
          <span>{summary.count} / {ANNUAL_TARGET} entries</span>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${colour}`} style={{ width: `${pct}%` }} />
        </div>
        {summary.count >= ANNUAL_TARGET && (
          <p className="text-xs text-emerald-300 flex items-center gap-1 mt-1">
            <CheckCircle2 className="h-3 w-3" />
            GPhC minimum met for {year}
          </p>
        )}
      </div>
    </div>
  );
}

export default function PharmacistCpdLog() {
  const [year, setYear] = useState(currentYear);
  const [activities, setActivities] = useState<CpdActivity[]>([]);
  const [summary, setSummary] = useState<CpdSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [activitiesRes, summaryRes] = await Promise.all([
        fetch(`/api/pharmacist/cpd?year=${year}`),
        fetch(`/api/pharmacist/cpd/summary?year=${year}`),
      ]);
      if (activitiesRes.ok) setActivities(await activitiesRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this CPD activity? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/pharmacist/cpd/${id}`, { method: "DELETE" });
      setActivities((prev) => prev.filter((a) => a.id !== id));
      load();
    } finally {
      setDeletingId(null);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleExport = () => {
    if (!summary || activities.length === 0) return;
    setExporting(true);
    try {
      exportCpdToPdf(activities, summary, year);
    } finally {
      setExporting(false);
    }
  };

  return (
    <PharmacistLayout title="CPD Activity Log">
      <div className="space-y-6">
        {summary && <SummaryBar summary={summary} year={year} />}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label htmlFor="pharm-cpd-year" className="text-sm font-medium text-slate-600">Year:</label>
            <select
              id="pharm-cpd-year"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting || loading || activities.length === 0}
              className="gap-2 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
              title={activities.length === 0 ? "Add CPD entries before exporting" : `Export ${year} CPD log as PDF for GPhC submission`}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export PDF
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-violet-700 hover:bg-violet-800 text-white gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Entry
            </Button>
          </div>
        </div>

        {showForm && (
          <AddActivityForm
            onSaved={() => { setShowForm(false); load(); }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-2xl" />)}
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Target className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No CPD entries logged for {year}</p>
            <p className="text-sm mt-1">Add your first entry to start tracking your GPhC CPD cycle.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <div key={a.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div
                    className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(expanded ? null : a.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpandedId(expanded ? null : a.id); }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${TYPE_COLORS[a.type]}`}>
                          {TYPE_LABELS[a.type]}
                        </span>
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          {CATEGORY_LABELS[a.category]}
                        </span>
                      </div>
                      <p className="font-semibold text-slate-900 text-sm">{a.title}</p>
                      <p className="text-xs text-slate-500">{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-violet-700">{a.hours}h</p>
                    </div>
                    {expanded ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </div>
                  {expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Learning Outcome</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{a.learningOutcome}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-1">Benefit to Patients / Public</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{a.patientBenefit}</p>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => handleDelete(a.id)}
                          disabled={deletingId === a.id}
                          className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          {deletingId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>GPhC Requirements:</strong> From 2024, pharmacists must record a minimum of 9 CPD entries per year using the GPhC CPD cycle (Reflect → Plan → Act → Evaluate). Entries must demonstrate how learning benefits patients and the public. CPD recorded here is for your personal reference only — you must also maintain your formal record on the GPhC portal.
        </div>
      </div>
    </PharmacistLayout>
  );
}
